import { useCallback, useRef } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'

const SUBGOAL_FIELDS = `
  subgoalId goalId ownerUserId createdBy title primaryFacet facets status notes tracking createdAt updatedAt
`

const CREATE_SUBGOAL = `
  mutation CreateSubgoal($goalId: ID!, $ownerUserId: ID, $input: CreateSubgoalInput!) {
    createSubgoal(goalId: $goalId, ownerUserId: $ownerUserId, input: $input) { ${SUBGOAL_FIELDS} }
  }
`

const UPDATE_SUBGOAL = `
  mutation UpdateSubgoal($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $input: UpdateSubgoalInput!) {
    updateSubgoal(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, input: $input) { ${SUBGOAL_FIELDS} }
  }
`

const DELETE_SUBGOAL = `
  mutation DeleteSubgoal($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID) {
    deleteSubgoal(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId)
  }
`

const SET_SUBGOAL_STATUS = `
  mutation SetSubgoalStatus($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $status: String!) {
    setSubgoalStatus(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, status: $status) { ${SUBGOAL_FIELDS} }
  }
`

const UPDATE_KPI = `
  mutation UpdateKpi($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $current: Int!) {
    updateKpi(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, current: $current) { ${SUBGOAL_FIELDS} }
  }
`

const TOGGLE_DELIVERABLE = `
  mutation ToggleDeliverable($goalId: ID!, $subgoalId: ID!, $deliverableId: ID!, $ownerUserId: ID) {
    toggleDeliverable(goalId: $goalId, subgoalId: $subgoalId, deliverableId: $deliverableId, ownerUserId: $ownerUserId) { ${SUBGOAL_FIELDS} }
  }
`

const ADD_DELIVERABLE = `
  mutation AddDeliverable($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $text: String!) {
    addDeliverable(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, text: $text) { ${SUBGOAL_FIELDS} }
  }
`

const TOGGLE_MILESTONE = `
  mutation ToggleMilestone($goalId: ID!, $subgoalId: ID!, $milestoneId: ID!, $ownerUserId: ID) {
    toggleMilestone(goalId: $goalId, subgoalId: $subgoalId, milestoneId: $milestoneId, ownerUserId: $ownerUserId) { ${SUBGOAL_FIELDS} }
  }
`

const ADD_MILESTONE = `
  mutation AddMilestone($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $title: String!, $targetDate: String) {
    addMilestone(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, title: $title, targetDate: $targetDate) { ${SUBGOAL_FIELDS} }
  }
`

const REMOVE_MILESTONE = `
  mutation RemoveMilestone($goalId: ID!, $subgoalId: ID!, $milestoneId: ID!, $ownerUserId: ID) {
    removeMilestone(goalId: $goalId, subgoalId: $subgoalId, milestoneId: $milestoneId, ownerUserId: $ownerUserId) { ${SUBGOAL_FIELDS} }
  }
`

const REMOVE_DELIVERABLE = `
  mutation RemoveDeliverable($goalId: ID!, $subgoalId: ID!, $deliverableId: ID!, $ownerUserId: ID) {
    removeDeliverable(goalId: $goalId, subgoalId: $subgoalId, deliverableId: $deliverableId, ownerUserId: $ownerUserId) { ${SUBGOAL_FIELDS} }
  }
`

const UPDATE_CRITERIA = `
  mutation UpdateCriteria($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $text: String!) {
    updateCriteria(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, text: $text) { ${SUBGOAL_FIELDS} }
  }
`

const UPDATE_DEADLINE = `
  mutation UpdateDeadline($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $date: String!) {
    updateDeadline(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, date: $date) { ${SUBGOAL_FIELDS} }
  }
`

const UPDATE_SUBGOAL_NOTES = `
  mutation UpdateSubgoalNotes($goalId: ID!, $subgoalId: ID!, $ownerUserId: ID, $notes: String!) {
    updateSubgoalNotes(goalId: $goalId, subgoalId: $subgoalId, ownerUserId: $ownerUserId, notes: $notes) { ${SUBGOAL_FIELDS} }
  }
`

const ADD_COMMENT = `
  mutation AddComment($goalId: ID!, $ownerUserId: ID, $text: String!) {
    addComment(goalId: $goalId, ownerUserId: $ownerUserId, text: $text) {
      entryId goalId createdBy createdByName text createdAt
    }
  }
`

const DELETE_COMMENT = `
  mutation DeleteComment($goalId: ID!, $entryId: ID!, $ownerUserId: ID) {
    deleteComment(goalId: $goalId, entryId: $entryId, ownerUserId: $ownerUserId)
  }
`

export function useSubgoal({ ownerUserId = null } = {}) {
  const debounceRef = useRef(null)
  const notesDebounceRef = useRef(null)

  const createSubgoal = useCallback(async (goalId, input) => {
    const data = await appsyncQuery(CREATE_SUBGOAL, { goalId, ownerUserId, input })
    return data.createSubgoal
  }, [ownerUserId])

  const updateSubgoal = useCallback(async (goalId, subgoalId, input) => {
    const data = await appsyncQuery(UPDATE_SUBGOAL, { goalId, subgoalId, ownerUserId, input })
    return data.updateSubgoal
  }, [ownerUserId])

  const deleteSubgoal = useCallback(async (goalId, subgoalId) => {
    await appsyncQuery(DELETE_SUBGOAL, { goalId, subgoalId, ownerUserId })
  }, [ownerUserId])

  const setSubgoalStatus = useCallback(async (goalId, subgoalId, status) => {
    const data = await appsyncQuery(SET_SUBGOAL_STATUS, { goalId, subgoalId, ownerUserId, status })
    return data.setSubgoalStatus
  }, [ownerUserId])

  const updateKpi = useCallback((goalId, subgoalId, current) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      await appsyncQuery(UPDATE_KPI, { goalId, subgoalId, ownerUserId, current })
    }, 800)
  }, [ownerUserId])

  const toggleDeliverable = useCallback(async (goalId, subgoalId, deliverableId) => {
    const data = await appsyncQuery(TOGGLE_DELIVERABLE, { goalId, subgoalId, deliverableId, ownerUserId })
    return data.toggleDeliverable
  }, [ownerUserId])

  const addDeliverable = useCallback(async (goalId, subgoalId, text) => {
    const data = await appsyncQuery(ADD_DELIVERABLE, { goalId, subgoalId, ownerUserId, text })
    return data.addDeliverable
  }, [ownerUserId])

  const toggleMilestone = useCallback(async (goalId, subgoalId, milestoneId) => {
    const data = await appsyncQuery(TOGGLE_MILESTONE, { goalId, subgoalId, milestoneId, ownerUserId })
    return data.toggleMilestone
  }, [ownerUserId])

  const addMilestone = useCallback(async (goalId, subgoalId, title, targetDate) => {
    const vars = { goalId, subgoalId, ownerUserId, title }
    if (targetDate) vars.targetDate = targetDate
    const data = await appsyncQuery(ADD_MILESTONE, vars)
    return data.addMilestone
  }, [ownerUserId])

  const removeMilestone = useCallback(async (goalId, subgoalId, milestoneId) => {
    const data = await appsyncQuery(REMOVE_MILESTONE, { goalId, subgoalId, milestoneId, ownerUserId })
    return data.removeMilestone
  }, [ownerUserId])

  const removeDeliverable = useCallback(async (goalId, subgoalId, deliverableId) => {
    const data = await appsyncQuery(REMOVE_DELIVERABLE, { goalId, subgoalId, deliverableId, ownerUserId })
    return data.removeDeliverable
  }, [ownerUserId])

  const updateCriteria = useCallback(async (goalId, subgoalId, text) => {
    const data = await appsyncQuery(UPDATE_CRITERIA, { goalId, subgoalId, ownerUserId, text })
    return data.updateCriteria
  }, [ownerUserId])

  const updateDeadline = useCallback(async (goalId, subgoalId, date) => {
    const data = await appsyncQuery(UPDATE_DEADLINE, { goalId, subgoalId, ownerUserId, date })
    return data.updateDeadline
  }, [ownerUserId])

  const updateSubgoalNotes = useCallback((goalId, subgoalId, notes) => {
    if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current)
    notesDebounceRef.current = setTimeout(async () => {
      await appsyncQuery(UPDATE_SUBGOAL_NOTES, { goalId, subgoalId, ownerUserId, notes })
    }, 800)
  }, [ownerUserId])

  const addComment = useCallback(async (goalId, text) => {
    const data = await appsyncQuery(ADD_COMMENT, { goalId, ownerUserId, text })
    return data.addComment
  }, [ownerUserId])

  const deleteComment = useCallback(async (goalId, entryId) => {
    await appsyncQuery(DELETE_COMMENT, { goalId, entryId, ownerUserId })
  }, [ownerUserId])

  return {
    createSubgoal,
    updateSubgoal,
    deleteSubgoal,
    setSubgoalStatus,
    updateKpi,
    toggleDeliverable,
    addDeliverable,
    removeDeliverable,
    toggleMilestone,
    addMilestone,
    removeMilestone,
    updateCriteria,
    updateDeadline,
    updateSubgoalNotes,
    addComment,
    deleteComment,
  }
}
